import React from 'react';
import usePageMeta from '../hooks/usePageMeta';

export default function AboutPage() {
    usePageMeta(
        'About Bliv Design Studio | Leading Residential Interior Designers',
        'Bliv Design Studio designs durable, customised interiors using plywood, HDHMR, laminates and acrylic finishes for homes across South India.'
    );
    const team = [
        { name: 'Suresh Kumar', role: 'Founder & Principal Designer', img: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400&q=80' },
        { name: 'Priya Sharma', role: 'Lead Interior Architect', img: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400&q=80' },
        { name: 'Ravi Teja', role: 'Project Manager', img: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&q=80' },
    ];

    const materials = [
        { name: 'MDF', desc: 'Medium Density Fiberboard – versatile, paintable, and ideal for modular units. Best used away from water sources.' },
        { name: 'Plywood', desc: 'Strong and durable with excellent moisture resistance. A premium choice for kitchen cabinets and structural elements.' },
        { name: 'Particleboard', desc: 'An economical option for interior elements that are not exposed to water or high humidity environments.' },
        { name: 'HDHMR', desc: 'High-Density High Moisture Resistant Boards – superior toughness, moisture & pest resistance. The industry preferred choice.' },
        { name: 'Laminate', desc: 'Highly resistant to stains, scratches, and moisture. Wide variety of colors and finishes for personalized aesthetics.' },
        { name: 'Acrylic', desc: 'Known for a premium high-gloss finish and contemporary appeal. Easy to clean with a stunning visual impact.' },
    ];

    return (
        <>
            {/* Hero */}
            <section className="relative pt-36 pb-20 bg-[#0D0D0D] overflow-hidden">
                <div className="absolute inset-0 opacity-20">
                    <img src="https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?w=1920&q=80" alt="" className="w-full h-full object-cover" />
                </div>
                <div className="relative max-w-7xl mx-auto px-4 text-center">
                    <p className="text-[#2E63E8] font-semibold uppercase tracking-widest text-sm mb-3">Who We Are</p>
                    <h1 className="text-5xl md:text-7xl font-bold text-white mb-6">About Bliv Design Studio</h1>
                    <p className="text-gray-300 text-lg max-w-3xl mx-auto leading-relaxed">
                        South India's most trusted luxury interior design studio. We transform spaces into timeless expressions of elegance.
                    </p>
                </div>
            </section>

            {/* Story */}
            <section className="section-pad bg-white">
                <div className="max-w-7xl mx-auto px-4 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                    <div>
                        <p className="text-[#2E63E8] font-semibold uppercase tracking-widest text-sm mb-3">Our Story</p>
                        <h2 className="text-4xl font-bold text-gray-900 mb-6">Crafting Beautiful Spaces Since 2014</h2>
                        <p className="text-gray-600 leading-relaxed mb-4">
                            Bliv Design Studio was founded with a singular vision: to make luxury interior design accessible to every homeowner in South India. Over a decade, we've grown from a small design studio in Hyderabad to a full-service interior design powerhouse serving clients across Telangana, Andhra Pradesh, Karnataka, and beyond.
                        </p>
                        <p className="text-gray-600 leading-relaxed mb-6">
                            We specialize in end-to-end interior solutions — from concept and 3D visualization to MEP drawings, execution, and final handover. Our services span residential projects (independent houses, flats, bungalows, duplex flats) to commercial and healthcare spaces.
                        </p>
                        <div className="grid grid-cols-3 gap-4">
                            {[['500+', 'Projects'], ['1000+', 'Clients'], ['5+', 'Cities']].map(([n, l]) => (
                                <div key={l} className="text-center bg-[#f0f4fe] rounded-xl p-4">
                                    <div className="text-3xl font-bold text-[#2E63E8]">{n}</div>
                                    <div className="text-gray-600 text-sm mt-1">{l}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="rounded-3xl overflow-hidden shadow-2xl">
                        <img src="https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&q=80" alt="Bliv Design Studio office" className="w-full h-[500px] object-cover" />
                    </div>
                </div>
            </section>

            {/* Materials */}
            <section className="section-pad bg-[#F8F9FA]">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="text-center mb-12">
                        <p className="text-[#2E63E8] font-semibold uppercase tracking-widest text-sm mb-2">Premium Quality</p>
                        <h2 className="text-4xl font-bold text-gray-900 mb-4">Materials We Use</h2>
                        <div className="w-20 h-1 bg-[#2E63E8] mx-auto rounded-full"></div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {materials.map((mat, i) => (
                            <div key={i} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:border-[#2E63E8] hover:shadow-md transition-all duration-300">
                                <div className="w-10 h-10 bg-[#f0f4fe] rounded-xl flex items-center justify-center mb-4">
                                    <span className="text-[#2E63E8] font-bold text-sm">{mat.name.slice(0, 2)}</span>
                                </div>
                                <h3 className="text-gray-900 font-bold mb-2">{mat.name}</h3>
                                <p className="text-gray-600 text-sm leading-relaxed">{mat.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Project Types */}
            <section className="section-pad bg-white">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="text-center mb-12">
                        <p className="text-[#2E63E8] font-semibold uppercase tracking-widest text-sm mb-2">Project Types</p>
                        <h2 className="text-4xl font-bold text-gray-900 mb-4">What We Design</h2>
                        <div className="w-20 h-1 bg-[#2E63E8] mx-auto rounded-full"></div>
                    </div>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
                        {[
                            { type: 'Independent House', img: 'https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=400&q=80' },
                            { type: 'Flat / Apartment', img: 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=400&q=80' },
                            { type: 'Bungalow', img: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=400&q=80' },
                            { type: 'Duplex Flat', img: 'https://images.unsplash.com/photo-1600047509807-ba8f99d2cdde?w=400&q=80' },
                        ].map((p, i) => (
                            <div key={i} className="relative rounded-2xl overflow-hidden h-56 group card-hover shadow-md">
                                <img src={p.img} alt={p.type} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end p-4">
                                    <span className="text-white font-bold text-sm">{p.type}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Team */}
            <section className="section-pad bg-[#f0f4fe]">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="text-center mb-12">
                        <p className="text-[#2E63E8] font-semibold uppercase tracking-widest text-sm mb-2">Meet Our Experts</p>
                        <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Design Team</h2>
                        <div className="w-20 h-1 bg-[#2E63E8] mx-auto rounded-full"></div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 max-w-4xl mx-auto">
                        {team.map((m, i) => (
                            <div key={i} className="bg-white rounded-2xl overflow-hidden shadow-md card-hover text-center">
                                <div className="h-52 overflow-hidden">
                                    <img src={m.img} alt={m.name} className="w-full h-full object-cover" />
                                </div>
                                <div className="p-5">
                                    <h3 className="font-bold text-gray-900">{m.name}</h3>
                                    <p className="text-[#2E63E8] text-sm mt-1">{m.role}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </>
    );
}
